import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { FlexLayoutModule } from "@angular/flex-layout";
import {
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from "@angular/forms";
import { MatOptionModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule, MatSelectTrigger } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { UntilDestroy } from "@ngneat/until-destroy";
import { compareMatch } from "@portal-workspace/grow-shared-library";
import {
  SearchableSelectItem,
  setupUntilDestroy
} from "@portal-workspace/grow-ui-library";
import { MARK } from "@portal-workspace/grow-ui-library/mark";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, tap } from "rxjs/operators";
import { AbstractControlValueAccessor } from "../abstract-control-value-accessor";
import { CommonModule } from "@angular/common";
@UntilDestroy({arrayName: 'subscriptions'})
@Component({
  selector: 'searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    FlexLayoutModule,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatSelectTrigger,
    CommonModule
  ],
  providers:[
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(()=>SearchableSelectComponent), multi: true},
    {provide: MARK, useExisting: forwardRef(()=>SearchableSelectComponent)}
  ],
})
export class SearchableSelectComponent extends AbstractControlValueAccessor<SearchableSelectItem[]> implements OnInit, OnChanges {

  subscriptions: Subscription[] = [];
  
  @Input({required: true}) formControl!: FormControl;
  @Input({required: true}) items!: SearchableSelectItem[];
  @Input({required: false}) multi: boolean = false;
  @Input({required: false}) placeholder: string | undefined= 'Select...';
  @Input({required: false}) label: string | undefined = undefined;
  @Input({required: false}) isEditable: boolean = true;

  @Output() selectionChange = new EventEmitter<SearchableSelectItem[]>();

  searchControl = new FormControl<string>('');
  filteredItems: SearchableSelectItem[] = [];
  displayValue!: string;

  _selectionChange(event: any, item: SearchableSelectItem) {
    if (event.isUserInput) {
      this.selectionChange.emit([item]);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.filteredItems = [...(this.items ?? [])];
      this.filteredItems = this.sortItems(this.filteredItems);
      this.displayValue = this.getTriggerValue();
    }
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
    this.displayValue = this.getTriggerValue();
    this.subscriptions.push(this.formControl.valueChanges.pipe(
      distinctUntilChanged(compareMatch),
      debounceTime(100),
      tap(r => {
        this.displayValue = this.getTriggerValue();
        this.propagateChange(r);
      })
    ).subscribe());

    this.subscriptions.push(this.searchControl.valueChanges.pipe(
      tap(v => {
        this.filteredItems = this.sortItems(this.filterItems(v));
      })
    ).subscribe());
  }

  compareFn = (a: any, b: any) => {
    if (!Array.isArray(a)) {
      return a === b;
    } else {
      return a[0] === b[0];
    }
  }

  sortItems(items: SearchableSelectItem[]): SearchableSelectItem[] {

    return items.sort((a, b) => {
      // Sort by count in descending order
      if (this.formControl.value.includes(a.value) == this.formControl.value.includes(b.value)) { // i.e. if both are selected or not selected
      const countComparison = Number(b.count ?? 0) - Number(a.count ?? 0); // Handle potential undefined counts
      return countComparison;
      }
      else if (this.formControl.value.includes(a.value)) return -1;
      else if (this.formControl.value.includes(b.value)) return 1;
      else return 0;
    });
    }
  

  onSearch(event: Event): void {
    const searchText = (event.target as HTMLInputElement).value.toLowerCase();
    this.items = this.filterItems(searchText);
  }

  private filterItems(searchText: string | null): SearchableSelectItem[] {
    if (!searchText) {
      return this.items;
    }
    const searchLower = searchText.toLowerCase();

    return this.items.filter(item => 
      (item.searchTerm?.toLowerCase()?.includes(searchLower) || 
       item.label.toLowerCase().includes(searchLower))
    );
  }

  doWriteValue(v: SearchableSelectItem[]) {
    const ids = this.formControl.value.map((item: SearchableSelectItem) => item.id);
    for (const item of v) {
      if (!ids.includes(item.id)) {
        this.formControl.setValue(v);
      }
    }
  }

  getTriggerValue(): string {
    if (!this.formControl.value) {
      return '';
    }
    return this.formControl.value.map((v: string) => this.items.find((i) => i.value === v)?.label).filter((v: string | undefined) => !!v).join(', ');
  }
}
